import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import QRCodeStyling from 'qr-code-styling'
import type { Options } from 'qr-code-styling'

export type QrCanvasHandle = {
  download: (extension: 'svg' | 'png' | 'jpeg', filename?: string) => Promise<void>
  /** Render the QR code at `size`×`size` px and return its pixel data. */
  getImageData: (size: number) => Promise<ImageData>
}

type QrCanvasProps = {
  // Options used for the visible canvas preview.
  options: Options
  // Options used for the hidden SVG/export instance (may differ in size).
  // Falls back to `options` when not provided.
  exportOptions?: Options
}

const QrCanvas = forwardRef<QrCanvasHandle, QrCanvasProps>(({ options, exportOptions }, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const qrCodeRef = useRef<QRCodeStyling | null>(null)

  // Hidden SVG instance — used only for vector/raster downloads
  const svgContainerRef = useRef<HTMLDivElement | null>(null)
  const svgQrCodeRef = useRef<QRCodeStyling | null>(null)

  // Visible canvas preview
  useEffect(() => {
    if (!containerRef.current) return
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options)
      containerRef.current.innerHTML = ''
      qrCodeRef.current.append(containerRef.current)
    } else {
      qrCodeRef.current.update(options)
    }
  }, [options])

  // Hidden SVG export instance (full-resolution)
  useEffect(() => {
    const opts = exportOptions ?? options
    const svgOptions: Options = { ...opts, type: 'svg' }
    if (!svgQrCodeRef.current) {
      svgQrCodeRef.current = new QRCodeStyling(svgOptions)
      if (svgContainerRef.current) {
        svgContainerRef.current.innerHTML = ''
        svgQrCodeRef.current.append(svgContainerRef.current)
      }
    } else {
      svgQrCodeRef.current.update(svgOptions)
    }
  }, [options, exportOptions])

  useImperativeHandle(ref, () => ({
    download: (extension: 'svg' | 'png' | 'jpeg', filename = 'qr-code') => {
      if (extension === 'svg') {
        const svgEl = svgContainerRef.current?.querySelector('svg')
        if (!svgEl) return Promise.resolve()
        const clone = svgEl.cloneNode(true) as SVGElement
        clone.querySelector(':scope > rect')?.remove()
        const svgStr = new XMLSerializer().serializeToString(clone)
        const blob = new Blob([svgStr], { type: 'image/svg+xml' })
        const href = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = href
        a.download = `${filename}.svg`
        a.click()
        URL.revokeObjectURL(href)
        return Promise.resolve()
      }
      // PNG/JPEG: spin up a temporary full-res canvas instance for download
      const opts = exportOptions ?? options
      const canvasInstance = new QRCodeStyling({ ...opts, type: 'canvas' })
      return canvasInstance.download({ name: filename, extension })
    },
    getImageData: async (size: number): Promise<ImageData> => {
      const opts = exportOptions ?? options
      const instance = new QRCodeStyling({ ...opts, type: 'canvas', width: size, height: size })
      // getRawData resolves only after the canvas is fully painted
      const blob = await instance.getRawData('png') as Blob
      const bitmap = await createImageBitmap(blob)
      const offscreen = document.createElement('canvas')
      offscreen.width = size
      offscreen.height = size
      const ctx = offscreen.getContext('2d')!
      ctx.drawImage(bitmap, 0, 0)
      return ctx.getImageData(0, 0, size, size)
    },
  }))

  return (
    <>
      <div ref={containerRef} />
      <div ref={svgContainerRef} style={{ display: 'none' }} aria-hidden="true" />
    </>
  )
})

QrCanvas.displayName = 'QrCanvas'

export default QrCanvas
